import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import profileAPI from '../api/profile';

const UploadCV = () => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const nextPage = location.state?.returnTo || '/jobs';
  
  // Check for recent CV on component mount
  useEffect(() => {
    const checkForRecentCV = async () => {
      try {
        // Check if user is coming from the menu
        const isFromMenu = location.state?.fromMenu === true;
        
        // Only redirect if not coming from menu
        if (!isFromMenu) {
          const response = await profileAPI.checkRecentCV();
          if (response.data.has_recent_cv) {
            // Skip to jobs page if recent CV exists
            navigate('/jobs');
          }
        }
      } catch (err) {
        console.error('Error checking for recent CV:', err);
        // Continue showing the upload page if there's an error
      }
    };
    
    checkForRecentCV();
  }, [navigate, location.state]);
  
  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Check if file is PDF
      if (file.type !== 'application/pdf') {
        setError('יש להעלות קובץ PDF בלבד');
        setSelectedFile(null);
        fileInputRef.current.value = '';
        return;
      }
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('גודל הקובץ חורג מ-10MB המותרים');
        setSelectedFile(null);
        fileInputRef.current.value = '';
        return;
      }
      
      setSelectedFile(file);
      setError('');
    }
  };
  
  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('אנא בחר קובץ להעלאה');
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      await profileAPI.uploadCV(selectedFile);
      
      setSuccess('קורות החיים הועלו בהצלחה');
      setSelectedFile(null);
      fileInputRef.current.value = '';
      
      // Continue after a short delay
      setTimeout(() => {
        navigate(nextPage);
      }, 2000);
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('שגיאה בהעלאת קורות החיים. אנא נסה שנית.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Skip CV upload
  const handleSkip = () => {
    navigate(nextPage);
  };

  return (
    <Layout>
      <div className="mx-auto w-full max-w-2xl px-4 py-6 sm:px-6 sm:py-8">
        <h1 className="mb-4 text-xl font-bold text-gray-900 sm:mb-6 sm:text-2xl">העלאת קורות חיים</h1>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        {/* Success Message */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}
        
        <div className="card">
          <div className="mb-6 rounded-md border border-yellow-300 bg-yellow-50 p-4">
            <p className="font-medium text-gray-900">אין לך קורות חיים מוכנים?</p>
            <Link to="/create-cv" state={{ returnTo: nextPage }} className="mt-2 inline-block font-medium text-yellow-800 underline">
              יצירת קורות חיים של Rebase
            </Link>
          </div>
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              העלאת קורות חיים תאפשר לנו להתאים לך משרות באופן מדויק יותר.
              <br />
              אנא העלה קובץ PDF בגודל של עד 10MB.
            </p>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf"
                className="hidden"
                id="cv-upload"
              />
              
              <label
                htmlFor="cv-upload"
                className="cursor-pointer flex flex-col items-center justify-center"
              >
                <svg
                  className="w-12 h-12 text-gray-400 mb-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <span className="text-gray-600 font-medium">לחץ כאן לבחירת קובץ</span>
                <span className="text-gray-500 text-sm mt-1">או גרור קובץ לכאן</span>
              </label>
              
              {selectedFile && (
                <div className="mt-4 text-sm">
                  <span className="font-medium">נבחר: </span>
                  <span className="text-blue-600">{selectedFile.name}</span>
                  <span className="text-gray-500 mr-2">
                    ({Math.round(selectedFile.size / 1024)} KB)
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-between">
            <button
              type="button"
              onClick={handleSkip}
              className="btn-secondary"
            >
              דלג
            </button>
            
            <button
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || isLoading}
              className={`btn-primary ${(!selectedFile || isLoading) ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? 'מעלה...' : 'העלה קורות חיים'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UploadCV;
